import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import { AlertCircle, Check, Loader2, Trophy, Search, User, X } from 'lucide-react';

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
  registration_code?: string;
}

interface AgeGroup {
  age_group_id: string;
  group_name: string;
  birth_years: number[];
}

interface Competition {
  event_id: string;
  event_name: string;
  gender_category: string;
  category?: {
    category_name: string;
  };
  age_group?: {
    group_name: string;
  };
}

interface ExistingParticipant {
  student_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  date_of_birth: string;
  gender: string;
  house_id: string;
  department_id: string;
  reference_number?: string;
  house_name: string;
  house_color: string;
}

interface RegistrationFormData {
  first_name: string;
  last_name: string;
  first_name_tamil: string;
  last_name_tamil: string;
  date_of_birth: string;
  gender: string;
  department_id: string;
  selected_competitions: string[];
}

const StandaloneRegistrationForm: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'code' | 'search' | 'form' | 'success'>('code');
  const [departmentCode, setDepartmentCode] = useState('');
  const [department, setDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState<RegistrationFormData>({
    first_name: '',
    last_name: '',
    first_name_tamil: '',
    last_name_tamil: '',
    date_of_birth: format(new Date(), 'yyyy-MM-dd'),
    gender: '',
    department_id: '',
    selected_competitions: []
  });
  
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [eligibleCompetitions, setEligibleCompetitions] = useState<Competition[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isModuleEnabled, setIsModuleEnabled] = useState(false);
  
  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<ExistingParticipant[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<ExistingParticipant | null>(null);

  useEffect(() => {
    checkModuleStatus();
    
    // Check if there's a code in the URL
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setDepartmentCode(codeFromUrl);
      // Auto-verify the code if it's present in the URL
      verifyDepartmentCodeFromUrl(codeFromUrl);
    }
  }, []);

  const checkModuleStatus = async () => {
    try {
      setLoading(true);
      
      // Use the public (anon) client to check the module status
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_name', 'public_registration_enabled')
        .maybeSingle();

      if (error) {
        console.error('Error checking module status:', error);
        setIsModuleEnabled(false);
        return;
      }

      setIsModuleEnabled(data?.setting_value?.enabled === true);
    } catch (error) {
      console.error('Error:', error);
      setIsModuleEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const verifyDepartmentCodeFromUrl = async (code: string) => {
    try {
      setVerifying(true);
      setError(null);

      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('registration_code', code.trim())
        .single();

      if (error || !data) {
        setError(t('publicRegistration.errors.invalidCode'));
        return;
      }

      setDepartment(data);
      setFormData(prev => ({ ...prev, department_id: data.department_id }));
      
      // Fetch age groups and competitions
      await Promise.all([
        fetchAgeGroups(),
        fetchCompetitions()
      ]);
      
      setStep('search');
    } catch (error: any) {
      console.error('Error verifying department code:', error);
      setError(error.message || t('publicRegistration.errors.verificationFailed'));
    } finally {
      setVerifying(false);
    }
  };

  const verifyDepartmentCode = async () => {
    if (!departmentCode.trim()) {
      setError(t('publicRegistration.errors.enterCode'));
      return;
    }

    try {
      setVerifying(true);
      setError(null);

      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('registration_code', departmentCode.trim())
        .single();

      if (error || !data) {
        setError(t('publicRegistration.errors.invalidCode'));
        return;
      }

      setDepartment(data);
      setFormData(prev => ({ ...prev, department_id: data.department_id }));
      
      // Fetch age groups and competitions
      await Promise.all([
        fetchAgeGroups(),
        fetchCompetitions()
      ]);
      
      setStep('search');
    } catch (error: any) {
      console.error('Error verifying department code:', error);
      setError(error.message || t('publicRegistration.errors.verificationFailed'));
    } finally {
      setVerifying(false);
    }
  };

  const fetchAgeGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('age_groups')
        .select('*')
        .order('birth_years', { ascending: false });
        
      if (error) {
        throw new Error(error.message);
      }
      
      setAgeGroups(data || []);
    } catch (error) {
      console.error('Error fetching age groups:', error);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          category:sports_categories(*),
          age_group:age_groups(*)
        `)
        .eq('status', 'not_started')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw new Error(error.message);
      }
      
      setCompetitions(data || []);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    }
  };

  const searchParticipants = async () => {
    if (!searchTerm.trim() || !department) return;
    
    try {
      setSearching(true);
      setError(null);
      
      // Call the RPC function to search participants
      const { data, error } = await supabase.rpc(
        'search_participants_by_name_or_reference',
        {
          search_term: searchTerm.trim(),
          department_id_param: department.department_id
        }
      );
      
      if (error) throw error;
      
      setSearchResults(data || []);
    } catch (error: any) {
      console.error('Error searching participants:', error);
      setError(error.message || 'Failed to search participants');
    } finally {
      setSearching(false);
    }
  };

  const selectExistingParticipant = (participant: ExistingParticipant) => {
    setSelectedParticipant(participant);
    
    // Pre-fill the form with participant data
    setFormData({
      first_name: participant.first_name,
      last_name: participant.last_name,
      first_name_tamil: participant.first_name_tamil || '',
      last_name_tamil: participant.last_name_tamil || '',
      date_of_birth: format(new Date(participant.date_of_birth), 'yyyy-MM-dd'),
      gender: participant.gender === 'M' ? 'M' : 'F',
      department_id: participant.department_id,
      selected_competitions: []
    });
    
    // Update eligible competitions based on the participant's data
    updateEligibleCompetitions(participant.date_of_birth, participant.gender);
    
    // Move to the form step
    setStep('form');
  };

  const updateEligibleCompetitions = (dateOfBirth: string, gender: string) => {
    if (!dateOfBirth || !gender || competitions.length === 0) {
      setEligibleCompetitions([]);
      return;
    }

    const birthYear = new Date(dateOfBirth).getFullYear();
    const genderCode = gender === 'M' || gender === 'Male' ? 'M' : 'F';

    const eligible = competitions.filter(comp => {
      // Check if competition is for the right gender
      const genderMatch = 
        comp.gender_category === genderCode || 
        comp.gender_category === 'A';

      // Check if competition is for the right age group
      const ageGroupMatch = comp.age_group?.birth_years?.includes(birthYear) || false;

      return genderMatch && ageGroupMatch;
    });

    setEligibleCompetitions(eligible);
  };

  useEffect(() => {
    if (formData.date_of_birth && formData.gender) {
      updateEligibleCompetitions(formData.date_of_birth, formData.gender);
    }
  }, [formData.date_of_birth, formData.gender, competitions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCompetitionToggle = (competitionId: string) => {
    setFormData(prev => {
      const selected = [...prev.selected_competitions];
      
      if (selected.includes(competitionId)) {
        return {
          ...prev,
          selected_competitions: selected.filter(id => id !== competitionId)
        };
      } else {
        return {
          ...prev,
          selected_competitions: [...selected, competitionId]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.selected_competitions.length === 0) {
      setError(t('publicRegistration.errors.selectCompetitions'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare the registration data with proper validation
      const registrationData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        first_name_tamil: formData.first_name_tamil.trim() || null,
        last_name_tamil: formData.last_name_tamil.trim() || null,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender, // This should already be 'M' or 'F'
        department_id: formData.department_id,
        competitions: formData.selected_competitions,
        status: 'pending'
      };

      // Validate required fields match RLS policy requirements
      if (!registrationData.first_name || 
          !registrationData.last_name || 
          !registrationData.date_of_birth || 
          !registrationData.gender || 
          !registrationData.department_id || 
          !registrationData.competitions || 
          registrationData.competitions.length === 0 ||
          registrationData.status !== 'pending') {
        throw new Error('Required fields are missing or invalid');
      }

      // If this is an existing participant, include the student_id
      if (selectedParticipant) {
        // Add the student_id to the registration data
        (registrationData as any).student_id = selectedParticipant.student_id;
      }

      // Insert the pending registration
      const { data, error } = await supabase
        .from('pending_registrations')
        .insert([registrationData])
        .select();

      if (error) throw error;

      setSuccess(true);
      setStep('success');
    } catch (error: any) {
      console.error('Error submitting registration:', error);
      setError(error.message || t('publicRegistration.errors.submissionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewParticipant = () => {
    setSelectedParticipant(null);
    setFormData({
      first_name: '',
      last_name: '',
      first_name_tamil: '',
      last_name_tamil: '',
      date_of_birth: format(new Date(), 'yyyy-MM-dd'),
      gender: '',
      department_id: department?.department_id || '',
      selected_competitions: []
    });
    setStep('form');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isModuleEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('publicRegistration.moduleDisabled.title')}
          </h2>
          <p className="text-gray-600">
            {t('publicRegistration.moduleDisabled.message')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-6">
          <Trophy className="h-12 w-12 text-blue-600" />
        </div>
        
        {/* Language Selector */}
        <div className="flex justify-center mb-6 space-x-2">
          <button
            onClick={() => i18n.changeLanguage('en')}
            className={`px-3 py-1 rounded text-sm ${i18n.language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            English
          </button>
          <button
            onClick={() => i18n.changeLanguage('nn')}
            className={`px-3 py-1 rounded text-sm ${i18n.language === 'nn' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Nynorsk
          </button>
          <button
            onClick={() => i18n.changeLanguage('ta')}
            className={`px-3 py-1 rounded text-sm ${i18n.language === 'ta' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            தமிழ்
          </button>
        </div>

        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {step === 'code' && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('publicRegistration.title')}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {t('publicRegistration.enterCodePrompt')}
                </p>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <label htmlFor="department_code" className="block text-sm font-medium text-gray-700">
                  {t('publicRegistration.departmentCode')}
                </label>
                <input
                  type="text"
                  id="department_code"
                  value={departmentCode}
                  onChange={(e) => setDepartmentCode(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('publicRegistration.codePlaceholder')}
                />
              </div>

              <div className="mt-6">
                <button
                  onClick={verifyDepartmentCode}
                  disabled={verifying}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {t('publicRegistration.verifying')}
                    </>
                  ) : (
                    t('publicRegistration.verify')
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'search' && department && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('publicRegistration.registerFor')} {department.name}
                </h2>
                {department.name_tamil && (
                  <p className="mt-1 text-lg text-gray-600" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                    {department.name_tamil}
                  </p>
                )}
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  {t('publicRegistration.searchExisting.title')}
                </p>
                
                <div className="flex space-x-2 mb-4">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('publicRegistration.searchExisting.placeholder')}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <button
                    onClick={searchParticipants}
                    disabled={searching || !searchTerm.trim()}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                  >
                    {searching ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Search className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {searchResults.length > 0 && (
                  <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700">Search Results</h3>
                    </div>
                    <ul className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                      {searchResults.map((participant) => (
                        <li 
                          key={participant.student_id}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => selectExistingParticipant(participant)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {participant.first_name} {participant.last_name}
                              </p>
                              {participant.first_name_tamil && participant.last_name_tamil && (
                                <p className="text-xs text-gray-500" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                                  {participant.first_name_tamil} {participant.last_name_tamil}
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {participant.reference_number ? `Ref: ${participant.reference_number}` : ''}
                              </p>
                            </div>
                            <div className="flex items-center">
                              <div 
                                className="h-3 w-3 rounded-full mr-2" 
                                style={{ backgroundColor: participant.house_color }}
                              />
                              <span className="text-xs text-gray-600">{participant.house_name}</span>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {searchTerm && searchResults.length === 0 && !searching && (
                  <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          {t('publicRegistration.searchExisting.noResults')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setStep('code');
                    setDepartmentCode('');
                    setDepartment(null);
                    setSearchResults([]);
                    setSearchTerm('');
                    // Remove the code parameter from the URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete('code');
                    window.history.replaceState({}, '', url.toString());
                  }}
                  className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('publicRegistration.back')}
                </button>
                <button
                  type="button"
                  onClick={handleNewParticipant}
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <User className="h-4 w-4 mr-2 inline" />
                  {t('publicRegistration.searchExisting.registerNew')}
                </button>
              </div>
            </div>
          )}

          {step === 'form' && department && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('publicRegistration.registerFor')} {department.name}
                </h2>
                {department.name_tamil && (
                  <p className="mt-1 text-lg text-gray-600" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                    {department.name_tamil}
                  </p>
                )}
                {selectedParticipant && (
                  <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <User className="h-4 w-4 mr-1" />
                    Existing Participant
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                    {t('participants.form.fields.firstName.label')}
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('participants.form.fields.firstName.placeholder')}
                    readOnly={!!selectedParticipant}
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                    {t('participants.form.fields.lastName.label')}
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('participants.form.fields.lastName.placeholder')}
                    readOnly={!!selectedParticipant}
                  />
                </div>

                <div>
                  <label htmlFor="first_name_tamil" className="block text-sm font-medium text-gray-700">
                    {t('participants.form.fields.firstNameTamil.label')}
                  </label>
                  <input
                    type="text"
                    id="first_name_tamil"
                    name="first_name_tamil"
                    value={formData.first_name_tamil}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('participants.form.fields.firstNameTamil.placeholder')}
                    readOnly={!!selectedParticipant}
                  />
                </div>

                <div>
                  <label htmlFor="last_name_tamil" className="block text-sm font-medium text-gray-700">
                    {t('participants.form.fields.lastNameTamil.label')}
                  </label>
                  <input
                    type="text"
                    id="last_name_tamil"
                    name="last_name_tamil"
                    value={formData.last_name_tamil}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('participants.form.fields.lastNameTamil.placeholder')}
                    readOnly={!!selectedParticipant}
                  />
                </div>

                <div>
                  <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700">
                    {t('participants.form.fields.dateOfBirth.label')}
                  </label>
                  <input
                    type="date"
                    id="date_of_birth"
                    name="date_of_birth"
                    required
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                    {t('participants.form.fields.gender.label')}
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    required
                    value={formData.gender}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    disabled={!!selectedParticipant}
                  >
                    <option value="" disabled>{t('participants.form.fields.gender.placeholder')}</option>
                    <option value="M">{t('participants.filters.male')}</option>
                    <option value="F">{t('participants.filters.female')}</option>
                  </select>
                </div>

                {eligibleCompetitions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('publicRegistration.eligibleCompetitions')}
                    </label>
                    <div className="space-y-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-md">
                      {eligibleCompetitions.map((competition) => (
                        <div key={competition.event_id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`comp-${competition.event_id}`}
                            checked={formData.selected_competitions.includes(competition.event_id)}
                            onChange={() => handleCompetitionToggle(competition.event_id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`comp-${competition.event_id}`} className="ml-2 block text-sm text-gray-900">
                            {competition.event_name} - {competition.category?.category_name} ({competition.age_group?.group_name})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {eligibleCompetitions.length === 0 && formData.date_of_birth && formData.gender && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          {t('publicRegistration.noEligibleCompetitions')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('search');
                      setSelectedParticipant(null);
                    }}
                    className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t('publicRegistration.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || formData.selected_competitions.length === 0}
                    className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        {t('publicRegistration.submitting')}
                      </>
                    ) : (
                      t('publicRegistration.submit')
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mt-3 text-lg font-medium text-gray-900">
                {t('publicRegistration.success.title')}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                {t('publicRegistration.success.message')}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setStep('code');
                    setDepartmentCode('');
                    setDepartment(null);
                    setFormData({
                      first_name: '',
                      last_name: '',
                      first_name_tamil: '',
                      last_name_tamil: '',
                      date_of_birth: format(new Date(), 'yyyy-MM-dd'),
                      gender: '',
                      department_id: '',
                      selected_competitions: []
                    });
                    setSuccess(false);
                    setSearchResults([]);
                    setSearchTerm('');
                    setSelectedParticipant(null);
                    // Remove the code parameter from the URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete('code');
                    window.history.replaceState({}, '', url.toString());
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('publicRegistration.registerAnother')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StandaloneRegistrationForm;